// =============================================================================
// Quick-count tiles — the one place that decides what a tile IS.
//
// A tile is a count the operator taps onto a pay credit. Two things feed the
// pad: JSON stored per tenant (farm_config / packhouse_config
// .piecework_quick_counts) and text typed on the rates page. Both go through
// sanitiseTiles, so the farm and the packhouse — and the prewarm task that
// warms the cache — read the same rule:
//   • numbers only — a stored string, boolean or nested array is not a tile
//     anyone chose (Number(true) is 1; Number([5]) is 5; neither is a tile);
//   • finite and positive, two decimals at most (the units column is
//     NUMERIC(12,2); a 0.001 tile would credit a row the DB rounds to 0.00);
//   • de-duplicated, ascending.
// Pure. Shared by useFarmConfig, usePackhouseQuickCounts, offlineLookups and
// the rates page's tile cards.
// =============================================================================

export type QuickCounts = Record<string, number[]>

export function sanitiseTiles(raw: unknown[]): number[] {
  const nums = raw
    .filter((t): t is number => typeof t === 'number' && Number.isFinite(t))
    .map(n => Math.round(n * 100) / 100)
    .filter(n => n >= 0.01)
  return Array.from(new Set(nums)).sort((a, b) => a - b)
}

/** Stored JSON → ladder per unit. Anything that is not an object of arrays
 *  becomes an empty ladder rather than a broken pad. */
export function shapeQuickCounts(raw: unknown): QuickCounts {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: QuickCounts = {}
  for (const [unit, tiles] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(tiles)) continue
    const nums = sanitiseTiles(tiles)
    if (nums.length) out[unit] = nums
  }
  return out
}

/** One unit's comma-separated text → tiles: "10, 1, 5, 5, x, -3" → [1, 5, 10]. */
export function parseTileDraft(text: string): number[] {
  return sanitiseTiles(text.split(',').map(s => parseFloat(s.trim())))
}
